import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatMoney, toMinorUnits } from "@tovi/shared";
import { Button, Modal, useTranslate } from "@tovi/ui";
import { api } from "@/lib/api.ts";
import { useTill } from "@/lib/use-till.ts";
import type { Cart } from "@/lib/cart.ts";
import { addressOf } from "@/lib/bills.ts";
import { useBills } from "@/lib/use-bills.ts";

/**
 * Settlement (`08-pos-terminal-and-qr.md` § A § Settlement).
 *
 * The spec lists split tender, dual currency, KHQR, discounts, tax-free,
 * credit, member balance, refund, reverse and merge/split. This screen covers
 * the ones a cashier reaches for on a normal bill — split tender across
 * methods, and change — and leaves the rest to the order surface, which
 * already implements and audits every one of them.
 *
 * The important behaviour is that **nothing is computed here**. The total came
 * from `pos-core`; what is tendered is arithmetic on it; and the settlement
 * itself goes through the offline queue as a money operation, so a lost
 * connection at the worst possible moment is a queued payment rather than a
 * lost one.
 *
 * **Built from the spec text.** Without a capture, the tender list is a
 * running total with a keypad; KHQR's scan-to-pay panel and the secondary
 * display are not built, and are marked below.
 */

type PaymentMethod = { id: string; code: string; name: string; isOffline: boolean };

type TipSettings = {
  isEnabled: boolean;
  suggestedRatesBp: number[];
  suggestOnPreTaxSubtotal: boolean;
};

/**
 * A suggested percentage, in the same integer arithmetic everything else here
 * uses.
 *
 * Rounded half-up to the minor unit, which is what a card terminal does and
 * what a guest expects to see: 18% of $42.35 is $7.62, not $7.6230. Never a
 * float — this is somebody's wage.
 */
function tipOf(base: bigint, rateBp: number): bigint {
  return (base * BigInt(rateBp) + 5000n) / 10_000n;
}

export function SettleDialog({ cart, onClose }: { cart: Cart; onClose: () => void }) {
  const t = useTranslate();
  const till = useTill();
  const { bills } = useBills();
  const [tenders, setTenders] = useState<{ methodId: string; name: string; amount: bigint }[]>([]);
  const [typed, setTyped] = useState("");
  const [tip, setTip] = useState(0n);
  const [tipTyped, setTipTyped] = useState("");

  const methods = useQuery({
    queryKey: ["payment-methods"],
    queryFn: () => api<{ data: PaymentMethod[] }>("/store/payment-methods").then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  /**
   * The tip settings, which a merchant may not have bought.
   *
   * A 402 is the ordinary answer here rather than an error: `retry: false` and
   * a disabled default mean a till without tipping simply never draws the row.
   */
  const tipSettings = useQuery({
    queryKey: ["tip-settings"],
    queryFn: () => api<TipSettings>("/store/tip-settings"),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const tipping = tipSettings.data?.isEnabled === true;

  const total = cart.priced?.total.amount ?? 0n;
  /**
   * What a suggested percentage is taken *of*.
   *
   * A real divergence rather than a preference: tipping on the pre-tax subtotal
   * is the older convention and still what many operators want on the button,
   * while most US card terminals suggest on the total. A few percent of
   * somebody's wage rides on getting it from the setting rather than guessing.
   */
  const tipBase = tipSettings.data?.suggestOnPreTaxSubtotal
    ? (cart.priced?.subtotal.amount ?? 0n)
    : total;

  const taken = tenders.reduce((sum, tender) => sum + tender.amount, 0n);
  /**
   * What still has to be tendered, measured against the **bill**.
   *
   * Deliberately not against the bill plus the tip: a tender's `amount` is what
   * it settles, and the server refuses one larger than the bill outstanding —
   * correctly, because a tip settles nothing. The gratuity travels beside the
   * tenders rather than inside them.
   */
  const outstanding = total - taken;
  const change = taken > total ? taken - total : 0n;
  // What the guest actually hands over. The two are added in exactly one place.
  const due = total + tip;

  function tender(method: PaymentMethod) {
    // What was typed, or whatever is left — the second is what a cashier
    // means nine times out of ten.
    const amount =
      typed.trim() === "" ? outstanding : BigInt(toMinorUnits(typed.trim(), cart.currency));
    if (amount <= 0n) return;
    setTenders([...tenders, { methodId: method.id, name: method.name, amount }]);
    setTyped("");
  }

  async function settle() {
    const bill = cart.bill;
    if (!bill) return;

    /**
     * The bill already exists — it was opened when the table was seated — so
     * settling is a payment and nothing else.
     *
     * Anything the station has not been told about goes first, in the same
     * order: a line that arrived after its own payment would land on a bill
     * the server has already closed.
     *
     * The payment is flagged as money: `08` § Offline mode is explicit that a
     * settlement is never resolved by last-write-wins.
     */
    await bills.fire();

    const address = addressOf(bill, "/pay");
    await till.till.queue(address.path, {
      method: "POST",
      isMoney: true,
      ...(address.dependsOn ? { dependsOn: address.dependsOn } : {}),
      body: {
        // The whole gratuity rides on the first tender rather than being split
        // across them. A split cheque with one tip is what a cashier means, and
        // the server accepts a tip per tender for the case where each guest
        // leaves their own — which is a screen this till does not have yet.
        tenders: tenders.map((row, index) => ({
          paymentMethodId: row.methodId,
          amount: row.amount.toString(),
          tip: (index === 0 ? tip : 0n).toString(),
        })),
      },
    });

    await till.till.drain();
    // Paid, so it leaves this till's book and the table derives back to free.
    await bills.close(bill.key);
    onClose();
  }

  return (
    <Modal open title={t("pos.settle.title")} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-ink-muted">{t("pos.settle.total")}</span>
          <span className="text-2xl font-semibold text-ink-body">
            {formatMoney(total.toString(), cart.currency)}
          </span>
        </div>

        {tenders.length > 0 ? (
          <ul className="flex flex-col gap-1 text-sm">
            {tenders.map((row, index) => (
              <li key={index} className="flex justify-between text-ink-body">
                <span>{row.name}</span>
                <span>{formatMoney(row.amount.toString(), cart.currency)}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {tipping ? (
          <div className="flex flex-col gap-2 border-t border-line pt-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-ink-muted">{t("pos.settle.tip")}</span>
              <span className="text-lg font-semibold text-ink-body">
                {formatMoney(tip.toString(), cart.currency)}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {(tipSettings.data?.suggestedRatesBp ?? []).map((rateBp) => (
                <Button
                  key={rateBp}
                  variant={tip === tipOf(tipBase, rateBp) && tip > 0n ? "primary" : "secondary"}
                  onClick={() => setTip(tipOf(tipBase, rateBp))}
                >
                  {`${(rateBp / 100).toFixed(rateBp % 100 === 0 ? 0 : 2)}%`}
                </Button>
              ))}
              <Button variant="secondary" onClick={() => setTip(0n)}>
                {t("pos.settle.noTip")}
              </Button>
            </div>

            {/* A guest may key any amount, and often does. The buttons are a
                convenience, never the only way through. */}
            <label className="flex flex-col gap-1 text-sm text-ink-muted">
              {t("pos.settle.tipOther")}
              <input
                inputMode="decimal"
                className="h-11 rounded-control border border-line bg-surface px-3 text-base text-ink-body"
                value={tipTyped}
                onChange={(event) => {
                  setTipTyped(event.target.value);
                  const raw = event.target.value.trim();
                  if (raw === "") return setTip(0n);
                  try {
                    setTip(BigInt(toMinorUnits(raw, cart.currency)));
                  } catch {
                    // Mid-typing is not an error state: "1." is on the way to
                    // "1.50", and clearing the tip under the cashier would be
                    // worse than waiting for a number.
                  }
                }}
              />
            </label>

            <div className="flex items-baseline justify-between">
              <span className="text-sm text-ink-muted">{t("pos.settle.due")}</span>
              <span className="text-lg font-semibold text-ink-body">
                {formatMoney(due.toString(), cart.currency)}
              </span>
            </div>
          </div>
        ) : null}

        <div className="flex items-baseline justify-between border-t border-line pt-2">
          <span className="text-sm text-ink-muted">
            {outstanding > 0n ? t("pos.settle.stillToPay") : t("pos.settle.change")}
          </span>
          <span className="text-lg font-semibold text-ink-body">
            {formatMoney((outstanding > 0n ? outstanding : change).toString(), cart.currency)}
          </span>
        </div>

        <label className="flex flex-col gap-1 text-sm text-ink-muted">
          {t("pos.settle.amountTendered")}
          <input
            inputMode="decimal"
            className="h-12 rounded-control border border-line bg-surface px-3 text-lg text-ink-body"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={formatMoney(outstanding.toString(), cart.currency)}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          {(methods.data ?? []).map((method) => (
            <Button key={method.id} variant="secondary" onClick={() => tender(method)}>
              {method.name}
            </Button>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("pos.settle.back")}
          </Button>
          <Button disabled={outstanding > 0n} onClick={settle}>
            {t("pos.settle.done")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
